import * as coreActions from '../actions/core.actions';
import * as principalsActions from '../actions/principals.actions';

import { Action, createReducer, on } from '@ngrx/store';
import {
  AppSection,
  NomLanguages,
  NomObjectClass,
  NomObjectLifecycleStatus,
  NomObjectTypes,
} from '../constants';
import {
  Dashboard,
  DataObject,
  Dimension,
  Feed,
  Language,
  ObjectDefinition,
  User,
  UserObjectsResponse,
  Workspace,
} from '../models/core.models';
import {
  augmentObjectWithIconAndImage,
  augmentObjects,
} from 'src/app/shared/utils/object-utils';
import {
  createFeed,
  createFeedSuccess,
  duplicateFeedsSuccess,
  editFeedSuccess,
  loadFeedSuccess,
} from '../actions/feed.actions';
import {
  duplicateDashboards,
  duplicateDashboardsSuccess,
  loadDashboardSuccess,
} from '../actions/dashboard.actions';
import {
  fetchUserObjects,
  fetchUserObjectsByIdsSuccess,
  fetchUserObjectsSuccess,
} from '../actions/object.actions';
import {
  loginSuccess,
  logout,
  switchUserCompany,
} from '../actions/auth.actions';

import { ModuleOption } from '../components/module-switch/module-switch.component';
import { Principal } from '../services/object.service';
import { SortDirection } from 'ag-grid-community';
import { UserAccessLevel } from '../services/user.service';
import { produce } from 'immer';
import { setHomeObjectsLifeCycle } from '../actions/home.actions';
import { setWorkspaceObjectsLifeCycle } from '../actions/workspace.actions';
import { toggleUserStatusSuccess } from 'src/app/admin/actions/admin-users.actions';

function updateArrayByKey<T>(
  stateArray: T[],
  newArray: T[],
  key: keyof T,
): T[] {
  return stateArray.map((stateItem) => {
    const index = newArray.findIndex(
      (existingItem) => existingItem[key] === stateItem[key],
    );
    if (index !== -1) {
      return { ...stateItem, ...newArray[index] };
    } else {
      return stateItem;
    }
  });
}

export interface CoreState {
  workspaces: Array<Workspace>;
  feeds: Array<Feed>;
  dashboards: Array<Dashboard>;
  dimensions: Array<Dimension>;
  objectsLoaded: boolean;
  workspacesLoaded: boolean;
  loading: boolean;
  error: any; // You can specify a more specific type if you have one for errors.
  darkMode: boolean;
  hasShadow: boolean;
  homeObjectsLifeCycle: number;
  workspaceObjectsLifeCycle: number;
  recentObjects: Array<DataObject>; // list of definitions of recent objects
  objects: Array<DataObject>; // list of all  objects that have being loaded for datasources by ids, used in object pickers
  filteredObjectsSearchString: string;
  filteredObjectsLoading: boolean;
  filteredObjects: Array<DataObject>; // list of filtered objects based on user search of filters, used in object pickers, search suggest, etc
  filteredObjectsShowSelectedOnly: boolean;
  filteredObjectsSortingField: string;
  filteredObjectsSortingDirection: SortDirection;
  appSection: AppSection;
  modules: ModuleOption[];
  language: Language;
  creatingFeed: boolean;
  toggledUserData: { id: string; isEnabled: boolean };
}

export const initialState: CoreState = {
  workspaces: [],
  feeds: [],
  dashboards: [],
  dimensions: [],
  loading: false,
  objectsLoaded: false,
  hasShadow: false,
  workspacesLoaded: false,
  error: null,
  darkMode: false,
  homeObjectsLifeCycle: NomObjectLifecycleStatus.ACTIVE,
  workspaceObjectsLifeCycle: NomObjectLifecycleStatus.ACTIVE,
  recentObjects: [],
  objects: [],
  filteredObjectsSearchString: '',
  filteredObjects: [],
  filteredObjectsLoading: false,
  filteredObjectsShowSelectedOnly: false,
  filteredObjectsSortingField: 'title',
  filteredObjectsSortingDirection: 'asc',
  appSection: 'analytics',
  modules: [
    {
      id: 1,
      name: 'Analytics',
      icon: ['fas', 'chart-column'],
      url: '/analytics',
      active: true,
      accessLevel: UserAccessLevel.CLIENT_USER,
    },
    {
      id: 2,
      name: 'Admin',
      icon: ['fas', 'lock'],
      url: '/admin',
      active: false,
      accessLevel: UserAccessLevel.CLIENT_ADMIN,
    },
    {
      id: 3,
      name: 'Source Catalog',
      icon: ['fas', 'rss'],
      url: '/source-catalog',
      active: false,
      accessLevel: UserAccessLevel.SYSTEM_ADMIN,
    },
    {
      id: 4,
      name: 'Sysadmin',
      icon: ['fas', 'screwdriver-wrench'],
      url: '/sysadmin',
      active: false,
      accessLevel: UserAccessLevel.SYSTEM_ADMIN,
    },
    {
      id: 5,
      name: 'Mobile App',
      icon: ['fas', 'mobile-screen-button'],
      url: '/mobile-admin',
      active: false,
      accessLevel: UserAccessLevel.PLATFORM_ADMIN,
    },
  ],

  language: {
    id: NomLanguages.ENGLISH,
    name: 'English',
    code: 'en',
    rtl: false,
  },
  creatingFeed: false,
  toggledUserData: null,
};
type WorkspaceObject = DataObject | ObjectDefinition;
// If the augmenter preserves the shape:
const mapAugment = <T extends WorkspaceObject>(arr?: T[]): T[] =>
  (arr ?? []).map((obj) => augmentObjectWithIconAndImage(obj) as T);

const reducer = createReducer(
  initialState,
  on(coreActions.fetchUserWorkspaces, (state) => ({
    ...state,
    workspaces: [],
    loading: true,
    error: null,
  })),

  on(coreActions.fetchUserWorkspacesSuccess, (state, { workspaces }) => {
    return {
      ...state,
      workspaces: mapAugment(workspaces),
      loading: false,
      workspacesLoaded: true,
    };
  }),

  on(coreActions.fetchWorkspaceObjects, (state) => ({
    ...state,
    feeds: [],
    dashboards: [],
    dimensions: [],
    loading: true,
    error: null,
  })),

  on(coreActions.fetchUserFeeds, (state) => ({
    ...state,
    feeds: [],
    loading: true,
    error: null,
  })),
  on(coreActions.fetchUserFeedsSuccess, (state) => ({
    ...state,
    feeds: [],
    loading: true,
    error: null,
  })),
  on(coreActions.fetchUserDashboards, (state) => ({
    ...state,
    dashboards: [],
    loading: true,
    error: null,
  })),

  on(coreActions.fetchUserDimensions, (state) => ({
    ...state,
    dimensions: [],
    loading: true,
    error: null,
  })),

  on(coreActions.fetchUserDashboardsSuccess, (state, { dashboards }) => {
    return {
      ...state,
      dashboards: dashboards,
      loading: false,
    };
  }),

  on(coreActions.fetchUserDimensionsSuccess, (state, { dimensions }) => {
    return {
      ...state,
      dimensions: dimensions,
      loading: false,
    };
  }),

  on(coreActions.fetchWorkspaceObjectsSuccess, (state, { objects }) => {
    return {
      ...state,
      feeds: mapAugment(objects.feeds),
      dashboards: mapAugment(objects.dashboards),
      dimensions: mapAugment(objects.dimensions),
      loading: false,
    };
  }),

  on(
    fetchUserObjectsByIdsSuccess,
    produce((draft, { objects }) => {
      for (const obj of objects) {
        const index = draft.objects.findIndex((o) => o.id === obj.id);
        const updatedObj = augmentObjectWithIconAndImage(obj);

        if (index !== -1) {
          draft.objects[index] = updatedObj;
        } else {
          draft.objects.push(updatedObj);
        }
      }
    }),
  ),
  // * Reset the state to initial state on logout
  on(logout, () => initialState),

  on(
    loadFeedSuccess,
    produce((draft, { feed }) => {
      const objectTypeId = feed.object_type_id;

      // * Decide which field to update: 'feeds' or 'dimensions'
      const targetList =
        objectTypeId === NomObjectTypes.SEARCH_DIMENSION
          ? draft.dimensions
          : draft.feeds;

      const index = targetList.findIndex((f) => f.id === feed.id);

      if (index !== -1) {
        targetList[index] = augmentObjectWithIconAndImage(feed);
      } else {
        targetList.push(augmentObjectWithIconAndImage(feed));
      }
    }),
  ),

  on(
    loadDashboardSuccess,
    produce((draft, { dashboard }) => {
      const index = draft.dashboards.findIndex((d) => d.id === dashboard.id);
      const enriched = augmentObjectWithIconAndImage(dashboard);

      if (index === -1) {
        draft.dashboards.push(enriched);
      } else {
        draft.dashboards[index] = enriched;
      }
    }),
  ),

  on(
    editFeedSuccess,
    produce((draft, { feedId, feed }) => {
      const targetList =
        feed.object_type_id === NomObjectTypes.SEARCH_DIMENSION
          ? draft.dimensions
          : draft.feeds;

      const index = targetList.findIndex((f) => f.id === feedId);

      // * feed not found, do nothing
      if (index === -1) return;

      targetList[index] = augmentObjectWithIconAndImage(feed);
    }),
  ),

  on(
    createFeed,
    produce((draft) => {
      draft.creatingFeed = true;
    }),
  ),

  on(
    createFeedSuccess,
    produce((draft, { feed }) => {
      const targetList =
        feed.object_type_id === NomObjectTypes.SEARCH_DIMENSION
          ? draft.dimensions
          : draft.feeds;

      targetList.push(feed);
      draft.creatingFeed = false;
    }),
  ),

  // on(
  //   createAndAttachDimensionSuccess,
  //   produce((draft, { dimension }) => {
  //     const targetList =
  //       dimension.object_type_id === NomObjectTypes.SEARCH_DIMENSION
  //         ? draft.dimensions
  //         : draft.feeds;

  //     targetList.push(dimension);
  //   }),
  // ),

  on(coreActions.toggleDarkMode, (state) => ({
    ...state,
    darkMode: !state.darkMode,
  })),

  on(loginSuccess, (state, { response }) => ({
    ...state,
    darkMode: response.user.dark_mode,
    language: response.user.language,
  })),

  on(
    coreActions.updateWorkspaceSuccess,
    produce((draft, { workspace }) => {
      const index = draft.workspaces.findIndex((w) => w.id === workspace.id);

      if (index !== -1) {
        draft.workspaces[index] = workspace;
      }
    }),
  ),

  on(
    coreActions.deleteWorkspaceSuccess,
    produce((draft, { workspaceId }) => {
      const index = draft.workspaces.findIndex((w) => w.id === workspaceId);

      if (index !== -1) {
        draft.workspaces.splice(index, 1);
      }
    }),
  ),

  on(principalsActions.loadCompanyPrincipals, (state) => ({
    ...state,
    error: null,
  })),

  on(
    coreActions.updateWorkspaceSuccess,
    produce((draft, { workspace }) => {
      const index = draft.workspaces.findIndex((w) => w.id === workspace.id);

      if (index !== -1) {
        draft.workspaces[index] = augmentObjectWithIconAndImage(workspace);
      }
    }),
  ),

  on(
    coreActions.createWorkspaceSuccess,
    produce((draft, { workspace }) => {
      draft.workspaces.push(augmentObjectWithIconAndImage(workspace));
    }),
  ),

  on(
    coreActions.createDashboardSuccess,
    produce((draft, { dashboard }) => {
      draft.dashboards.push(augmentObjectWithIconAndImage(dashboard));
    }),
  ),

  on(
    coreActions.updateDashboardSuccess,
    produce((draft, { dashboard }) => {
      const index = draft.dashboards.findIndex((d) => d.id === dashboard.id);
      if (index !== -1) {
        // * preserve selected_page_id and other runtime state during Dashboard update
        const currentDashboard = draft.dashboards[index];

        const updatedDashboard = {
          ...dashboard,
          selected_page_id: currentDashboard.selected_page_id,
        };
        draft.dashboards[index] =
          augmentObjectWithIconAndImage(updatedDashboard);
      }
    }),
  ),

  on(
    coreActions.deleteDashboardSuccess,
    produce((draft, { dashboardId }) => {
      const index = draft.dashboards.findIndex((d) => d.id === dashboardId);
      if (index !== -1) {
        draft.dashboards.splice(index, 1);
      }
    }),
  ),

  on(
    coreActions.moveObjectsSuccess,
    produce((draft, { objects }) => {
      draft.feeds = updateArrayByKey(draft.feeds, objects.data.feeds, 'id');

      draft.dimensions = updateArrayByKey(
        draft.dimensions,
        objects.data.dimensions,
        'id',
      );
    }),
  ),

  on(
    coreActions.massObjectOperationSuccess,
    produce((draft, { response }) => {
      const operations = ['unarchive', 'undelete', 'delete', 'archive'];

      if (operations.includes(response.operation)) {
        const feedIds = response.data.feeds.map((f) => f.id);
        const workspaceIds = response.data.workspaces.map((w) => w.id);
        const dashboardIds = response.data.dashboards.map((d) => d.id);
        const dimensionIds = response.data.dimensions.map((d) => d.id);

        draft.feeds = draft.feeds.filter((f) => !feedIds.includes(f.id));
        draft.workspaces = draft.workspaces.filter(
          (w) => !workspaceIds.includes(w.id),
        );
        draft.dashboards = draft.dashboards.filter(
          (d) => !dashboardIds.includes(d.id),
        );
        draft.dimensions = draft.dimensions.filter(
          (d) => !dimensionIds.includes(d.id),
        );
      }
    }),
  ),

  on(
    coreActions.objectChanged,
    produce((draft, { object }) => {
      if (!object) return;

      draft.recentObjects = [
        augmentObjectWithIconAndImage(object),
        ...draft.recentObjects.filter((o) => o.object_id !== object.id),
      ];
    }),
  ),

  on(
    coreActions.loadUserRecentObjectsSuccess,
    produce((draft, { recentObjects }) => {
      draft.recentObjects = recentObjects.map(augmentObjectWithIconAndImage);
    }),
  ),

  on(
    duplicateFeedsSuccess,
    produce((draft, { response }) => {
      draft.feeds.push(...augmentObjects(response.feeds));
    }),
  ),

  on(fetchUserObjects, (state, { query }) => {
    return {
      ...state,
      filteredObjectsLoading: true,
      filteredObjectsSearchString: query,
    };
  }),

  on(fetchUserObjectsSuccess, (state, { objects: filteredObjects }) => {
    return {
      ...state,
      filteredObjectsLoading: false,
      filteredObjects: augmentObjects(filteredObjects),
    };
  }),

  on(coreActions.clearFilteredObjects, (state) => {
    return {
      ...state,
      filteredObjects: [],
    };
  }),

  on(coreActions.showShadow, (state) => {
    return {
      ...state,
      hasShadow: true,
    };
  }),

  on(coreActions.hideShadow, (state) => {
    return {
      ...state,
      hasShadow: false,
    };
  }),

  on(
    coreActions.removeSearchSuggestion,
    produce((draft, { suggestionId }) => {
      const index = draft.recentObjects.findIndex(
        (item) => item.id === suggestionId,
      );
      if (index !== -1) {
        draft.recentObjects.splice(index, 1);
      }
    }),
  ),

  on(coreActions.setAppSection, (state, { appSection, menuItems }) => {
    return {
      ...state,
      appSection: appSection,
    };
  }),

  on(setHomeObjectsLifeCycle, (state, { homeObjectsLifeCycle }) => {
    return {
      ...state,
      homeObjectsLifeCycle,
    };
  }),

  on(setWorkspaceObjectsLifeCycle, (state, { workspaceObjectsLifeCycle }) => {
    return {
      ...state,
      workspaceObjectsLifeCycle,
    };
  }),
  on(
    coreActions.switchActiveModule,
    produce((draft, { moduleId }) => {
      for (const module of draft.modules) {
        module.active = module.id === moduleId;
      }
    }),
  ),

  on(coreActions.setCoreLanguage, (state, { language }) => {
    return {
      ...state,
      language,
    };
  }),

  on(switchUserCompany, (state) => {
    return {
      ...state,
      workspaces: [],
      feeds: [],
      dashboards: [],
      dimensions: [],
      users: [],
      groups: [],
      loading: true,
      error: null,
    };
  }),

  on(
    coreActions.setCoreFilteredObjectsSortingField,
    (state, { sortingField }) => {
      return {
        ...state,
        filteredObjectsSortingField: sortingField,
      };
    },
  ),

  on(
    coreActions.setCoreFilteredObjectsSortingDirection,
    (state, { sortingDirection }) => {
      return {
        ...state,
        filteredObjectsSortingDirection: sortingDirection,
      };
    },
  ),

  on(
    toggleUserStatusSuccess,
    produce((draft, { userId, isEnabled }) => {
      draft.toggledUserData = {
        id: userId,
        isEnabled: !isEnabled,
      };
    }),
  ),

  on(
    duplicateDashboardsSuccess,
    produce((draft, { response }) => {
      draft.dashboards.push(...augmentObjects(response.dashboards));
    }),
  ),
);

export function coreReducer(state: CoreState | undefined, action: Action) {
  return reducer(state, action);
}
